// FAFI Touch T1 (FAFI-Touch-Plan §2.5) — the morphing verb cluster, pure. The panel is a VIEW of
// `contextMapper.active()`, never a second opinion: `layoutFor` names, for each fixed slot, the action
// that is live in the given context, and the pseudo-code that action is bound to. The DOM layer renders
// exactly this; the bits come from the REAL ContextMapper fed with these codes (T-TCH-005 parity).
//
// Pseudo-code design (why not one code per slot): the mapper resolves shared codes by CONTEXT, so a
// code may be shared only where contexts genuinely gate the pair — Touch:A (pass ↔ call, the Space
// duality), Touch:D (loft ↔ hard tackle, the E duality), Touch:E (shield ↔ block, the Ctrl duality).
// LONG/SLIDE and the safe TACKLE are context-FREE actions; sharing a code would co-fire their bits in
// every context, so each gets its own code and the PANEL decides which one the physical slot emits.
// A pressed pointer keeps the code it went down with — the panel morphing never rewrites a live hold
// (the mapper's own press-latch guards the bits the same way).

import type { Bindings, ActionBinding } from '../bindings';
import type { MapContext } from '../contextMapper';

export const TOUCH_CODE = {
  A: 'Touch:A',
  B: 'Touch:B',
  LONG: 'Touch:long',
  SLIDE: 'Touch:slide',
  TACKLE: 'Touch:tackle',
  D: 'Touch:D',
  E: 'Touch:E',
  SPRINT: 'Touch:sprint',
  DIVE: 'Touch:dive',
  PUNCH: 'Touch:punch',
  GKL: 'Touch:gkL',
  GKR: 'Touch:gkR',
} as const;

/** The touch layout as a `Bindings` blob — the SAME shape the keyboard uses, so the ContextMapper,
 *  `BINDING_BTN` and `CONTEXT_ACTIONS` apply verbatim. Movement/switch/cheat-sheet carry no code:
 *  the stick, the SW slot callback and the meta buttons handle them outside the bit path (D127 rule). */
export const TOUCH_BINDINGS: Bindings = {
  shoot: TOUCH_CODE.B,
  passDirect: TOUCH_CODE.A,
  call: TOUCH_CODE.A,
  passLong: TOUCH_CODE.LONG,
  loft: TOUCH_CODE.D,
  tackleHard: TOUCH_CODE.D,
  tackle: TOUCH_CODE.TACKLE,
  slide: TOUCH_CODE.SLIDE,
  block: TOUCH_CODE.E,
  shield: TOUCH_CODE.E,
  sprint: TOUCH_CODE.SPRINT,
  diveFlop: TOUCH_CODE.DIVE,
  punch: TOUCH_CODE.PUNCH,
  shove: '', // unbound on touch exactly as on desktop (PT4-P14)
  gkDiveL: TOUCH_CODE.GKL,
  gkDiveR: TOUCH_CODE.GKR,
  moveUp: '',
  moveDown: '',
  moveLeft: '',
  moveRight: '',
  switchPlayer: '',
  cheatSheet: '',
};

export type SlotStyle = 'primary' | 'std' | 'small' | 'danger' | 'paddle';
export type SlotId = 'A' | 'B' | 'C' | 'D' | 'E' | 'SW' | 'DIVE' | 'PUNCH' | 'GKL' | 'GKR';

export interface SlotSpec {
  readonly slot: SlotId;
  /** Pseudo-code this slot emits while pressed ('' = no sim bit; the layer handles it, e.g. SWITCH). */
  readonly code: string;
  /** The action the label names — 'switch' for the request that is not a sim verb. */
  readonly action: ActionBinding | 'switch';
  readonly label: string;
  readonly sub?: string;
  readonly style: SlotStyle;
}

const PENALTY_LAYOUT: readonly SlotSpec[] = [
  { slot: 'GKL', code: TOUCH_CODE.GKL, action: 'gkDiveL', label: 'DIVE ◀', style: 'paddle' },
  { slot: 'GKR', code: TOUCH_CODE.GKR, action: 'gkDiveR', label: 'DIVE ▶', style: 'paddle' },
];

/** The slots for a context. Geometry is fixed (a slot never moves between contexts); labels morph.
 *  D159: `darkArts` gates DIVE/PUNCH — the first playtest said "too many buttons", so the signature
 *  mischief survives as a pause-menu opt-in instead of default clutter. */
export function layoutFor(ctx: MapContext, darkArts = false): readonly SlotSpec[] {
  if (ctx.defendingPenalty) return PENALTY_LAYOUT; // the paddles replace the cluster (T-TCH-014)
  const defend = ctx.ball === 'opponent';
  const a: SlotSpec = defend
    ? { slot: 'A', code: TOUCH_CODE.TACKLE, action: 'tackle', label: 'TACKLE', sub: 'safe', style: 'primary' }
    : ctx.ball === 'teammate'
      ? { slot: 'A', code: TOUCH_CODE.A, action: 'call', label: 'CALL', style: 'primary' }
      : { slot: 'A', code: TOUCH_CODE.A, action: 'passDirect', label: 'PASS', style: 'primary' };
  const c: SlotSpec = defend
    ? { slot: 'C', code: TOUCH_CODE.SLIDE, action: 'slide', label: 'SLIDE', style: 'std' }
    : { slot: 'C', code: TOUCH_CODE.LONG, action: 'passLong', label: 'LONG', style: 'std' };
  const d: SlotSpec = defend
    ? { slot: 'D', code: TOUCH_CODE.D, action: 'tackleHard', label: 'HARD', sub: 'can foul', style: 'danger' }
    : { slot: 'D', code: TOUCH_CODE.D, action: 'loft', label: 'LOFT', sub: 'tap = lob', style: 'std' };
  const e: SlotSpec =
    ctx.ball === 'mine'
      ? { slot: 'E', code: TOUCH_CODE.E, action: 'shield', label: 'SHIELD', sub: 'hold', style: 'small' }
      : { slot: 'E', code: TOUCH_CODE.E, action: 'block', label: 'BLOCK', sub: 'hold', style: 'small' };
  return [
    a,
    { slot: 'B', code: TOUCH_CODE.B, action: 'shoot', label: 'SHOOT', ...(ctx.ball === 'mine' ? { sub: 'hold = aim' } : {}), style: 'std' },
    c,
    d,
    e,
    // defending is when switching matters — same slot, more presence (FAFI-Touch-Plan §2.5)
    { slot: 'SW', code: '', action: 'switch', label: 'SWITCH', style: defend ? 'primary' : 'small' },
    ...(darkArts
      ? ([
          { slot: 'DIVE', code: TOUCH_CODE.DIVE, action: 'diveFlop', label: 'DIVE', style: 'small' },
          { slot: 'PUNCH', code: TOUCH_CODE.PUNCH, action: 'punch', label: 'PUNCH', style: 'danger' },
        ] as const)
      : []),
  ];
}

/**
 * T-TCH-004 — the layout hysteresis latch. A context change is adopted only after it persists
 * `hystMs`; a possession scramble therefore cannot strobe the labels. The FIRST context adopts
 * immediately (there is nothing to protect at mount). Flipping back cancels the pending change.
 */
export class LayoutLatch {
  private current: MapContext | null = null;
  private pending: MapContext | null = null;
  private pendingSince = 0;

  constructor(private readonly hystMs: number = 200) {}

  resolve(ctx: MapContext, nowMs: number): MapContext {
    const cur = this.current;
    if (cur === null) {
      this.current = ctx;
      return ctx;
    }
    const same = (a: MapContext, b: MapContext): boolean => a.ball === b.ball && a.defendingPenalty === b.defendingPenalty;
    if (same(ctx, cur)) {
      this.pending = null; // the scramble resolved back — no queued stale morph (T-TCH-004)
      return cur;
    }
    if (this.pending === null || !same(ctx, this.pending)) {
      this.pending = ctx;
      this.pendingSince = nowMs;
      return cur;
    }
    if (nowMs - this.pendingSince >= this.hystMs) {
      this.current = ctx;
      this.pending = null;
      return ctx;
    }
    return cur;
  }
}
